import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ChatAssistant } from '@/components/Form/ChatAssistant';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { RiskStrip } from '@/components/Ui/RiskStrip';
import { scrollToSection, UiLink } from '@/components/Ui/UiLink';
import { useFormOverview } from '@/hooks/useFormOverview';
import { useInstantSave } from '@/hooks/useInstantSave';
import { parseAndValidateFormSpec } from '@/lib/form-specs';
import { AfterSubmissionSection } from './AfterSubmissionSection';
import { AutoReplySection } from './AutoReplySection';
import { DeleteSection } from './DeleteSection';
import { DeveloperSection } from './DeveloperSection';
import { FieldsSection } from './FieldsSection';
import classes from './FormOverview.module.css';
import { IntegrationsSection } from './IntegrationsSection';
import { OverviewHeader } from './OverviewHeader';
import { ProtectionSection } from './ProtectionSection';
import { RecentMessagesSection } from './RecentMessagesSection';
import { RecipientsSection } from './RecipientsSection';
import { riskItems } from './riskItems';
import { SiteSection } from './SiteSection';
import { asMethod, type SectionProps } from './types';

/** The form page: one ledger in a fixed order, every switch saving at once. */
export function FormOverview({ formId }: { formId: string }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const data = useFormOverview(formId);
  const { reloadStatus } = data;
  const onSaved = useCallback(() => void reloadStatus(), [reloadStatus]);
  const { form, save, errors, setErrors } = useInstantSave(data.form, onSaved);
  const [chatOpen, setChatOpen] = useState(false);

  const spec = useMemo(() => {
    if (!form?.specs?.trim()) return null;
    const parsed = parseAndValidateFormSpec(form.specs);
    return parsed.formSpec && Object.keys(parsed.formSpec.fields).length > 0
      ? parsed.formSpec
      : null;
  }, [form?.specs]);

  const loaded = !!form;
  // Fix links elsewhere point at `/account/forms/:id#section`; land on that row once rendered.
  useEffect(() => {
    if (!loaded || !location.hash) return;
    const id = location.hash.slice(1);
    const frame = requestAnimationFrame(() => scrollToSection(id));
    return () => cancelAnimationFrame(frame);
  }, [loaded, location.hash]);

  if (!form) {
    return (
      <Page width="narrow">
        <PageHeader
          title={data.notFound ? t('form.notFound') : t('form.pageTitle')}
          breadcrumb={[{ label: t('forms.pageTitle'), to: '/account/forms' }]}
        />
        {data.notFound ? (
          <EmptyText>
            {t('form.notFoundBody')} <UiLink to="/account/forms">{t('form.allForms')}</UiLink>
          </EmptyText>
        ) : (
          <EmptyText>{t('ui.loading')}</EmptyText>
        )}
      </Page>
    );
  }

  const hasMessages = (data.summary?.messages_total ?? 0) > 0 || (data.messages?.length ?? 0) > 0;
  const props: SectionProps = {
    form,
    save,
    errors,
    setErrors,
    data,
    spec,
    method: asMethod(form.method),
    hasMessages,
  };

  return (
    <div className={classes.layout}>
      <div className={classes.main}>
        <Page width="narrow">
          <OverviewHeader
            form={form}
            save={save}
            errors={errors}
            data={data}
            chatOpen={chatOpen}
            onToggleChat={() => setChatOpen(!chatOpen)}
          />
          <RiskStrip
            items={riskItems(t, i18n.language, {
              form,
              problems: data.problems,
              summary: data.summary,
              spec,
              hasMessages,
            })}
          />
          <RecipientsSection form={form} data={data} />
          <ProtectionSection {...props} />
          <SiteSection {...props} />
          <FieldsSection {...props} />
          <AutoReplySection {...props} />
          <IntegrationsSection form={form} data={data} />
          <AfterSubmissionSection form={form} save={save} errors={errors} data={data} />
          <RecentMessagesSection form={form} data={data} />
          <DeveloperSection form={form} save={save} spec={spec} />
          <DeleteSection form={form} data={data} />
        </Page>
      </div>
      <ChatAssistant form={form} opened={chatOpen} onToggle={() => setChatOpen(false)} />
    </div>
  );
}
